import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TaskDetails } from "../../App";
import { TaskResultsViewer } from "../../components/task/TaskResultsViewer";
import { getTaskCardClasses } from "../../utils/taskStyles";
import { useModalHistory } from "../../utils/historyNav";
import { PaginationControls } from "../../components/ui/PaginationControls";
import { B2BTaskBadge, B2B_CONFIRM_TOAST } from "../../components/b2b/B2BBadges";
import { B2BDocumentsBlock } from "../../components/b2b/B2BDocumentsBlock";
import { useSingleFlight } from "../../utils/singleFlight";
import { ExpiredBadge, ExtendDeadlineButton } from "../../components/task/DeadlineControls";
import { canCancelByExpiry, deadlineParticipates, DEADLINE_ACTIVE_STATUSES } from "../../utils/deadline";
import { confirmAndCancelTask, cancelErrorText, isReopenedTask } from "../../utils/taskCancel";
import { ACTIVE_TASK_STATUSES, taskStatusLabel, disputeStatusLabel } from "../../utils/statusLabels";
import {
 Clock, CreditCard, MessageCircle, CheckCircle, RefreshCw, ClipboardList,
 CheckCheck, XCircle, Scale, AlertTriangle, Wrench, HelpCircle,
 MessageSquare, Ban, AlertCircle, Filter, ArrowUpDown, ChevronDown,
 ArrowUpRight, Hourglass
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MyTasks = ({ user, showToast, setShowAppReviewModal, setReviewTaskId }) => {
 const [tasks, setTasks] = useState([]);
 const { run: runOnce, isBusy } = useSingleFlight();
 const [cancelNotice, setCancelNotice] = useState(null);
 const [loading, setLoading] = useState(true);
 const [selectedTask, setSelectedTask] = useState(null);
 const [disputes, setDisputes] = useState([]); // Добавляем состояние для spoров
 
 // ПЕРЕНОСИМ uploadFiles сюда чтобы избежать сброса state
 const [uploadFiles, setUploadFiles] = useState([]);
 // Оригинальные имена мелких файлов (параллельно uploadFiles), чтобы сохранить осмысленные названия
 const [uploadFileNames, setUploadFileNames] = useState([]);

 // Открытая карточка задания создаёт запись истории: жест "назад" возвращает к списку, а не из приложения
 useModalHistory(!!selectedTask, () => {
 setSelectedTask(null);
 setUploadFiles([]);
 setUploadFileNames([]);
 fetchMyTasks();
 });

 // Новые состояния для фильтрации (НЕ ВЛИЯЮТ на существующий код)
 const [filter, setFilter] = useState('active'); // 'active', 'awaiting', 'completed', 'all'
 const [sortBy, setSortBy] = useState('date'); // 'date', 'price', 'status'

 const changeFilter = (f) => {
 setFilter(f);
 setCurrentPage(1);
 };

 // Пагинация
 const [currentPage, setCurrentPage] = useState(1);
 const [perPage, setPerPage] = useState(10);

 // УДАЛЕНЫ дублирующие состояния - используем из MainContent через props

 // Состояние для сворачивания раздела "Результат:" в карточках заданий
 const [collapsedResults, setCollapsedResults] = useState({});
 const toggleResultCollapsed = (taskId) => {
 setCollapsedResults(prev => ({ ...prev, [taskId]: !prev[taskId] }));
 };

 // Состояние для модального окна жалобы
 const [showDisputeModal, setShowDisputeModal] = useState(false);
 const [selectedDisputeTask, setSelectedDisputeTask] = useState(null);
 const [disputeForm, setDisputeForm] = useState({
 reason: '',
 description: ''
 });

 // Состояние для модального окна возврата на доработку
 const [showRevisionModal, setShowRevisionModal] = useState(false);
 const [revisionTask, setRevisionTask] = useState(null);
 const [revisionComment, setRevisionComment] = useState('');
 const [revisionSubmitting, setRevisionSubmitting] = useState(false);

 // Иконки статусов (Lucide)
 const STATUS_ICONS = {
 pending: Clock,
 pending_payment: CreditCard,
 discussing: MessageCircle,
 accepted: CheckCircle,
 in_progress: RefreshCw,
 completed: ClipboardList,
 confirmed: CheckCheck,
 cancelled: XCircle,
 escrow: Scale,
 disputed: AlertTriangle,
 revision_requested: Wrench,
 dispute_resolved_customer: Scale,
 open: RefreshCw,
 };

 // Функции для улучшенных статус-индикаторов
 const getStatusInfo = (status) => {
 const statusMap = {
 pending: {
 text: 'Ожидает исполнителя',
 class: 'reversia-status-pending',
 progress: 10
 },
 pending_payment: {
 text: 'Требует оплаты',
 class: 'reversia-status-payment',
 progress: 5,
 showPayButton: true
 },
 discussing: {
 text: 'Обсуждается',
 class: 'reversia-status-discussing',
 progress: 25
 },
 accepted: {
 text: 'Принято к выполнению',
 class: 'reversia-status-accepted',
 progress: 50
 },
 in_progress: {
 text: 'Выполняется',
 class: 'reversia-status-in-progress',
 progress: 75
 },
 completed: {
 text: 'Ожидает подтверждения',
 class: 'reversia-status-completed',
 progress: 90
 },
 confirmed: {
 text: 'Подтверждено и завершено',
 class: 'reversia-status-confirmed',
 progress: 100
 },
 cancelled: {
 text: 'Отменено',
 class: 'reversia-status-cancelled',
 progress: 0
 },
 escrow: {
 text: 'На рассмотрении (спор)',
 class: 'reversia-status-escrow',
 progress: 15
 },
 disputed: {
 text: 'Спор открыт',
 class: 'reversia-status-escrow',
 progress: 15
 },
 revision_requested: {
 text: 'На доработке',
 class: 'reversia-status-discussing',
 progress: 60
 },
 dispute_resolved_customer: {
 text: 'Спор разрешён',
 class: 'reversia-status-completed',
 progress: 85
 },
 open: {
 text: taskStatusLabel('open'),
 class: 'reversia-status-pending',
 progress: 10
 }
 };
 
 return statusMap[status] || {
 text: taskStatusLabel(status),
 class: 'reversia-status-pending',
 progress: 0
 };
 };

 const renderStatusIndicator = (task) => {
 const statusInfo = getStatusInfo(task.status);
 const StatusIcon = STATUS_ICONS[task.status] || HelpCircle;

 return (
 <div className="flex flex-col gap-2">
 <div className={`reversia-status ${statusInfo.class}`}>
 <StatusIcon size={13} className="reversia-status-icon flex-shrink-0" />
 <span>{statusInfo.text}</span>
 </div>
 
 {/* Прогресс-бар */}
 <div className="reversia-progress-bar">
 <div 
 className={`reversia-progress-fill ${task.status}`}
 style={{ width: `${statusInfo.progress}%` }}
 ></div>
 </div>
 
 {/* Процент выполнения */}
 <div className="text-xs text-gray-500 text-right">
 {statusInfo.progress}% завершено
 </div>
 </div>
 );
 };

 const fetchMyTasks = useCallback(async () => {
 try {
 setLoading(true);
 const response = await axios.get(`${API}/tasks/my`);
 setTasks(response.data);
 } catch (error) {
 console.error("Error fetching tasks:", error);
 showToast("Ошибка при загрузке заданий", "error");
 } finally {
 setLoading(false);
 }
 }, [showToast]);

 const fetchMyDisputes = useCallback(async () => {
 try {
 const response = await axios.get(`${API}/disputes/my`);
 setDisputes(response.data);
 } catch (error) {
 console.error("Error fetching disputes:", error);
 // Не показываем ошибку - споры могут отсутствовать
 }
 }, []);

 // Load tasks on mount
 useEffect(() => {
 console.log(' MyTasks useEffect запущен');
 fetchMyTasks();
 fetchMyDisputes();
 
 // Проверяем нужно ли показать форму отзыва о приложении при загрузке
 console.log(' Установка таймера на 2 секунды...');
 setTimeout(() => {
 console.log(' Таймер сработал - запуск проверки отзыва');
 checkAppReviewForm(null);
 }, 2000);
 }, [fetchMyTasks, fetchMyDisputes]);

 // Ж12: переход из уведомления (колокольчик/настройки) — открыть карточку задания
 useEffect(() => {
 let focusId = null;
 try { focusId = sessionStorage.getItem("rv_focus_task"); } catch (e) {}
 if (!focusId || !tasks.length) return;
 const t = tasks.find((x) => x.id === focusId);
 try { sessionStorage.removeItem("rv_focus_task"); } catch (e) {}
 if (t) openTaskDetails(t);
 }, [tasks]);

 // Функция проверки нужно ли показать форму отзыва о приложении
 const checkAppReviewForm = async (taskId) => {
 try {
 console.log(' Запуск проверки отзыва о приложении...');
 
 // Проверяем пропустил ли пользователь отзыв в течение последнего часа
 const skipTimestamp = localStorage.getItem('app_review_skipped');
 if (skipTimestamp) {
 const hourAgo = Date.now() - (60 * 60 * 1000); // 1 час
 if (parseInt(skipTimestamp) >hourAgo) {
 console.log('ℹ Пользователь недавно пропустил отзыв, не показываем снова');
 return;
 }
 }
 
 const API = import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
 const token = localStorage.getItem('token');
 
 const response = await axios.get(`${API}/api/app-reviews/should-show`, {
 headers: { 'Authorization': `Bearer ${token}` }
 });

 console.log(' API ответ получен:', response.data);

 if (response.data.should_show) {
 console.log(' Показываем форму отзыва о приложении:', response.data);
 setReviewTaskId(taskId);
 setShowAppReviewModal(true);
 } else {
 console.log('ℹ Форма отзыва не показана:', response.data.reason);
 }
 } catch (error) {
 console.error('Error checking app review form:', error);
 }
 };

 const confirmTask = (taskId) => runOnce(`confirm:${taskId}`, async () => {
 try {
 const r = await axios.post(`${API}/tasks/${taskId}/confirm`);
 showToast(r.data?.already_done ? "Задание уже подтверждено." : r.data?.b2b ? B2B_CONFIRM_TOAST : "Задание подтверждено! Оплата выпущена.", "success");
 fetchMyTasks();
 if (!r.data?.already_done) setTimeout(() =>checkAppReviewForm(taskId), 1000);
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при подтверждении задания", "error");
 }
 });

 const openDisputeModal = (task) => {
 setSelectedDisputeTask(task);
 setShowDisputeModal(true);
 setDisputeForm({ reason: '', description: '' });
 };

 const openRevisionModal = (task) => {
 setRevisionTask(task);
 setRevisionComment('');
 setShowRevisionModal(true);
 };

 const closeRevisionModal = () => {
 setShowRevisionModal(false);
 setRevisionTask(null);
 setRevisionComment('');
 };

 const submitRevisionRequest = async () => {
 if (!revisionComment.trim()) {
 showToast("Опишите, что именно нужно исправить", "error");
 return;
 }
 try {
 setRevisionSubmitting(true);
 const response = await axios.post(`${API}/tasks/${revisionTask.id}/request-revision`, { comment: revisionComment });
 showToast(response.data?.message || "Задание возвращено исполнителю на доработку", "success");
 closeRevisionModal();
 fetchMyTasks();
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при возврате на доработку", "error");
 } finally {
 setRevisionSubmitting(false);
 }
 };

 const closeDisputeModal = () => {
 setShowDisputeModal(false);
 setSelectedDisputeTask(null);
 setDisputeForm({ reason: '', description: '' });
 };

 const submitDispute = async () => {
 if (!disputeForm.reason.trim() || !disputeForm.description.trim()) {
 showToast("Заполните все поля жалобы", "error");
 return;
 }

 try {
 await axios.post(`${API}/payments/dispute`, {
 task_id: selectedDisputeTask.id,
 reason: disputeForm.reason,
 description: disputeForm.description
 });
 
 showToast("Жалоба подана успешно. Администрация рассмотрит её в течение 3 дней.", "success");
 closeDisputeModal();
 fetchMyTasks(); // Обновляем список заданий
 } catch (error) {
 const errorMessage = error.response?.data?.detail || "Ошибка при подаче жалобы";
 showToast(errorMessage, "error");
 }
 };

 const cancelTask = (taskId) => runOnce(`cancel:${taskId}`, async () => {
 const task = tasks.find((t) => t.id === taskId) || {};
 try {
 const result = await confirmAndCancelTask(task);
 if (!result) return;
 setCancelNotice({ text: result.text, tone: result.tone, title: task.title || "" });
 showToast(result.text, result.tone, 12000);
 fetchMyTasks();
 } catch (error) {
 showToast(cancelErrorText(error), "error");
 }
 });

 // Clear unread count when opening task details
 const openTaskDetails = (task) => {
 console.log(' Opening task details:', task);
 if (!task || !task.id) {
 console.error(' Invalid task data:', task);
 showToast('Ошибка: некорректные данные задания', 'error');
 return;
 }
 setSelectedTask(task);
 };

 // Helper function to find dispute for a task
 const getTaskDispute = (taskId) => {
 const task = tasks.find(t =>t.id === taskId);
 const reopenedAt = task?.reopened_at ? new Date(task.reopened_at) : null;
 return disputes.find(dispute => {
 if (dispute.task_id !== taskId) return false;
 if (dispute.status === 'archived_on_reopen') return false;
 if (reopenedAt && dispute.created_at && new Date(dispute.created_at) < reopenedAt) return false;
 return true;
 });
 };

 // Helper function to get dispute status text and color
 const getDisputeStatusInfo = (dispute) => {
 if (!dispute) return null;
 
 const statusMap = {
 pending: { text: "В ожидании", color: "bg-yellow-100 text-yellow-800" },
 auto_resolved: { text: "Автоматически решен", color: "bg-blue-100 text-blue-800" },
 manual_review: { text: "На рассмотрении", color: "bg-orange-100 text-orange-800" },
 resolved: { text: "Решен", color: "bg-green-100 text-green-800" },
 awaiting_customer_action: { text: disputeStatusLabel("awaiting_customer_action"), color: "bg-teal-100 text-teal-800" }
 };
 
 const status = statusMap[dispute.status] || { text: disputeStatusLabel(dispute.status), color: "bg-gray-100 text-gray-800" };
 
 // Add winner information
 let winnerText = "";
 if (dispute.resolved_in_favor_of) {
 if (dispute.resolved_in_favor_of === user.id) {
 winnerText = " в вашу пользу";
 status.color = "bg-green-100 text-green-800";
 } else {
 winnerText = " не в вашу пользу";
 status.color = "bg-red-100 text-red-800";
 }
 }
 
 return {
 ...status,
 text: status.text + winnerText
 };
 };

 // Вспомогательные функции для фильтрации (НЕ ИЗМЕНЯЮТ существующую логику)
 const getFilteredTasks = () => {
 let filteredTasks = [...tasks]; // Копия массива, не изменяем оригинал

 // Фильтрация по статусу
 if (filter === 'active') {
 filteredTasks = filteredTasks.filter(task => ACTIVE_TASK_STATUSES.includes(task.status));
 } else if (filter === 'awaiting') {
 filteredTasks = filteredTasks.filter(task => task.status === 'completed');
 } else if (filter === 'completed') {
 filteredTasks = filteredTasks.filter(task => 
 ['confirmed', 'cancelled'].includes(task.status)
 );
 }
 // Для 'all' показываем все задания без фильтрации

 // Сортировка
 filteredTasks.sort((a, b) => {
 switch (sortBy) {
 case 'date':
 return new Date(b.created_at) - new Date(a.created_at); // Новые сначала
 case 'price':
 return b.price - a.price; // Дороже сначала
 case 'status':
 const statusOrder = ['pending', 'discussing', 'accepted', 'in_progress', 'completed', 'confirmed', 'cancelled'];
 return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
 default:
 return 0;
 }
 });

 return filteredTasks;
 };

 if (loading && !selectedTask && tasks.length === 0) {
 return (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
   <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin"
    style={{borderColor:'var(--rv-teal)', borderTopColor:'transparent'}} />
   <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>Загрузка заданий...</p>
  </div>
 );
 }

 if (selectedTask) {
 try {
 return (
 <TaskDetails 
 task={selectedTask} 
 user={user} 
 onBack={() => {
 setSelectedTask(null);
 // Очищаем файлы при выходе из задания
 setUploadFiles([]);
 setUploadFileNames([]);
 // Refresh tasks after closing task details
 fetchMyTasks();
 }} 
 onTaskUpdate={fetchMyTasks} 
 showToast={showToast}
 uploadFiles={uploadFiles}
 setUploadFiles={setUploadFiles} 
 uploadFileNames={uploadFileNames}
 setUploadFileNames={setUploadFileNames}
 />
 );
 } catch (error) {
 console.error('Error rendering TaskDetails:', error);
 showToast('Ошибка отображения задания', 'error');
 setSelectedTask(null);
 return (
 <div className="text-center p-8">
 <h2 className="text-xl font-semibold text-red-600 mb-2">Ошибка загрузки</h2>
 <p className="text-gray-600 mb-4">Произошла ошибка при загрузке задания</p>
 <button
 onClick={() =>setSelectedTask(null)}
 className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
 >
 ← Вернуться к списку заданий
 </button>
 </div>
 );
 }
 }

 const filteredMyTasks = getFilteredTasks();
 const paginatedMyTasks = filteredMyTasks.slice((currentPage - 1) * perPage, currentPage * perPage);

 return (
 <div className="space-y-6">
 <h2 className="text-2xl font-bold" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>Мои задания</h2>
 {cancelNotice && (
 <div data-testid="cancel-inline-status" role="status" className="rounded-lg p-4 flex items-start justify-between gap-3"
 style={cancelNotice.tone === 'error' ? {background:'#FEF2F2', border:'1px solid #FCA5A5', color:'#7F1D1D'} : {background:'#F0FDFA', border:'1px solid #5EEAD4', color:'#134E4A'}}>
 <div className="text-sm">
 <p className="font-semibold mb-1">{cancelNotice.title ? `Задание «${cancelNotice.title}» отменено` : 'Задание отменено'}</p>
 <p>{cancelNotice.text}</p>
 </div>
 <button data-testid="cancel-inline-status-close" onClick={() => setCancelNotice(null)} className="text-xs underline shrink-0">Скрыть</button>
 </div>
 )}
 
 {/* Фильтры и сортировка */}
 {tasks.length > 0 && (
 <div className="reversia-card">
 {/* Фильтры-вкладки */}
 <div className="flex flex-wrap gap-2 mb-4">
 <button
 data-testid="filter-active-btn"
 onClick={() =>changeFilter('active')}
 className="reversia-btn reversia-btn-md flex items-center gap-1.5"
 style={filter === 'active'
 ? {background:'var(--rv-teal)', color:'white'}
 : {background:'transparent', color:'var(--rv-text-secondary)', border:'1px solid var(--rv-border)'}}
 >
 <RefreshCw size={13} />Активные ({tasks.filter(t => ACTIVE_TASK_STATUSES.includes(t.status)).length})
 </button>
 <button
 data-testid="filter-awaiting-btn"
 onClick={() =>changeFilter('awaiting')}
 className="reversia-btn reversia-btn-md flex items-center gap-1.5"
 style={filter === 'awaiting'
 ? {background:'#d97706', color:'white'}
 : {background:'transparent', color:'var(--rv-text-secondary)', border:'1px solid var(--rv-border)'}}
 >
 <Hourglass size={13} />Ждут подтверждения ({tasks.filter(t => t.status === 'completed').length})
 </button>
 <button
 data-testid="filter-completed-btn"
 onClick={() =>changeFilter('completed')}
 className="reversia-btn reversia-btn-md flex items-center gap-1.5"
 style={filter === 'completed'
 ? {background:'#059669', color:'white'}
 : {background:'transparent', color:'var(--rv-text-secondary)', border:'1px solid var(--rv-border)'}}
 >
 <CheckCheck size={13} />Завершённые ({tasks.filter(t => ['confirmed', 'cancelled'].includes(t.status)).length})
 </button>
 <button
 data-testid="filter-all-btn"
 onClick={() =>changeFilter('all')}
 className="reversia-btn reversia-btn-md flex items-center gap-1.5"
 style={filter === 'all'
 ? {background:'var(--rv-navy)', color:'white'}
 : {background:'transparent', color:'var(--rv-text-secondary)', border:'1px solid var(--rv-border)'}}
 >
 <ClipboardList size={13} />Все ({tasks.length})
 </button>
 </div>

 {/* Сортировка */}
 <div className="flex items-center gap-2">
 <ArrowUpDown size={14} style={{color:'var(--rv-text-muted)'}} className="flex-shrink-0" />
 <span className="text-sm" style={{color:'var(--rv-text-secondary)', fontFamily:'IBM Plex Sans,sans-serif'}}>Сортировка:</span>
 <select
 value={sortBy}
 onChange={(e) =>setSortBy(e.target.value)}
 className="text-sm px-2 py-1 rounded-md" style={{border:'1px solid var(--rv-border)', color:'var(--rv-text-primary)', fontFamily:'IBM Plex Sans,sans-serif', background:'var(--rv-white)'}}
 >
 <option value="date">По дате</option>
 <option value="price">По цене</option>
 <option value="status">По статусу</option>
 </select>
 </div>
 </div>
 )}
 
 {tasks.length === 0 ? (
 <div className="text-center py-8">
 <p className="text-gray-600">У вас пока нет заданий</p>
 </div>
 ) : (<>
 <div className="grid gap-6">
 {paginatedMyTasks.map((task, index) => (
 <div key={task.id} className={`${getTaskCardClasses(task)} overflow-hidden fade-in transition-shadow duration-200 hover:shadow-lg`} style={{animationDelay: `${index * 0.1}s`}}>
 <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-4">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <h3 
 data-testid={`task-card-title-${task.id}`}
 className="text-lg font-semibold break-words leading-snug cursor-pointer transition-colors inline-flex items-start gap-1 group hover:underline underline-offset-4" style={{color:'var(--rv-teal)', fontFamily:'Manrope,sans-serif', textDecorationColor:'var(--rv-teal)'}}
 onClick={() =>openTaskDetails(task)}
 title="Открыть задание"
 >
 <span>{task.title}</span>
 <ArrowUpRight size={16} className="flex-shrink-0 mt-1 opacity-60 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
 </h3>
 {/* Removed duplicate unread indicator - it's now only on the button */}
 </div>
 <p className="text-xs mb-1" style={{color:'var(--rv-text-muted)'}}>Нажмите на название, чтобы открыть задание</p>
 {task.is_b2b && <div className="mb-1"><B2BTaskBadge /></div>}
 <p className="text-sm break-words" style={{color:'var(--rv-text-secondary)'}}>{task.location}</p>
 </div>
 <div className="text-left sm:text-right flex-shrink-0">
 <div className="text-lg font-bold text-green-600 mb-2">{task.price} ₽</div>
 {task.deadline && (
 <div className="text-xs mb-1" style={{color:'var(--rv-text-muted)'}}>До: {new Date(task.deadline).toLocaleDateString('ru-RU')}
 </div>
 )}
 <div className="mb-1"><ExpiredBadge task={task} /></div>
 {renderStatusIndicator(task)}
 </div>
 </div>
 
 {/* Dispute indicator - only show for active (unresolved) disputes */}
 {(() => {
 const dispute = getTaskDispute(task.id);
 if (!dispute || dispute.status === 'resolved' || dispute.status === 'auto_resolved' || dispute.status === 'archived_on_reopen') return null;
 const statusInfo = getDisputeStatusInfo(dispute);
 return statusInfo ? (
 <div className={`text-xs px-2 py-1 rounded mt-1 ${statusInfo.color}`}>Спор: {statusInfo.text}
 </div>
 ) : null;
 })()}
 
 <p className="text-sm mb-4 leading-relaxed" style={{color:'var(--rv-text-secondary)'}}>{task.description}</p>
 
 {(((task.media_files && task.media_files.length > 0) && !((task.reopened_at || task.previous_executor_id) && ['open', 'discussing', 'accepted'].includes(task.status))) || (task.external_links && task.external_links.length > 0)) && (
 <div className="mt-4 rounded-lg overflow-hidden" style={{border:'1px solid var(--rv-border)'}}>
 <div
 className="flex items-center justify-between px-3 py-2 cursor-pointer select-none" style={{background:'var(--rv-paper)'}}
 onClick={() =>toggleResultCollapsed(task.id)}
 >
 <h4 className="text-sm font-medium" style={{color:'var(--rv-text-primary)',fontFamily:'IBM Plex Sans,sans-serif'}}>Результат: ({(task.media_files?.length || 0) + (task.external_links?.length || 0)} файлов)
 </h4>
 <span className="text-xs font-medium" style={{color:'var(--rv-text-muted)'}}>
 {collapsedResults[task.id] ? '▼ Развернуть' : '▲ Свернуть'}
 </span>
 </div>
 {!collapsedResults[task.id] && (
 <div className="p-3">
 <TaskResultsViewer 
 taskId={task.id} 
 mediaFiles={task.media_files} 
 externalLinks={task.external_links}
 userRole={task.customer_id === user.id ? 'customer' : 'executor'}
 taskStatus={task.status}
 showToast={showToast}
 instanceId={`compact-${task.id}`}
 onTaskUpdate={fetchMyTasks}
 />
 </div>
 )}
 </div>
 )}
 
 <div className="mt-4 pt-3 flex flex-wrap gap-2" style={{borderTop:'1px solid var(--rv-border)'}}>
 <button
 data-testid={`mytasks-open-chat-${task.id}`}
 onClick={() =>openTaskDetails(task)}
 className="reversia-btn reversia-btn-teal reversia-btn-md"
 >
 <MessageSquare size={14} className="inline mr-1.5" />Открыть чат
 </button>
 
 {/* КНОПКА ОПЛАТЫ ДЛЯ YUKASSA */}
 {task.status === "pending_payment" && !task.is_b2b && (user.role === "customer" || user.role === "admin") && (
 <button
 onClick={() => {
 if (task.payment_url) {
 window.open(task.payment_url, '_blank');
 showToast("Переход к оплате через YuKassa...", "info");
 } else {
 showToast("Ссылка на оплату отсутствует", "error");
 }
 }}
 className="reversia-btn reversia-btn-green reversia-btn-md animate-pulse"
 >
 <CreditCard size={14} className="inline mr-1.5" />Оплатить задание
 </button>
 )}
 {task.status === "pending_payment" && !task.is_b2b && (user.role === "customer" || user.role === "admin") && (
 <span data-testid={`refund-hint-payment-${task.id}`} className="text-xs self-center" style={{color:'var(--rv-teal)'}}>
 Деньги замораживаются, а не уходят исполнителю. Передумали — отмените задание, вернём всю сумму.
 </span>
 )}
 
 {/* КНОПКА ОТМЕНЫ ЗАДАНИЯ */}
 {(task.status === "pending" || task.status === "discussing" || task.status === "pending_payment" || (task.status === "open" && isReopenedTask(task)) || (canCancelByExpiry(task) && task.customer_id === user.id)) && (user.role === "customer" || user.role === "admin") && (
 <button
 data-testid={`mytasks-cancel-task-${task.id}`}
 onClick={() =>cancelTask(task.id)}
 className="reversia-btn reversia-btn-danger-soft reversia-btn-md"
 >
 <Ban size={14} className="inline mr-1.5" />{(canCancelByExpiry(task) || (isReopenedTask(task) && ["open", "discussing"].includes(task.status))) && !task.is_b2b ? "Отменить с возвратом" : "Отменить задание"}
 </button>
 )}
 {task.customer_id === user.id && deadlineParticipates(task) && DEADLINE_ACTIVE_STATUSES.includes(task.status) && (
 <ExtendDeadlineButton task={task} showToast={showToast} onDone={fetchMyTasks} />
 )}
 
 {/* Details button for completed/confirmed tasks */}
 {(task.status === "completed" || task.status === "confirmed") && (
 <button
 onClick={() =>openTaskDetails(task)}
 className="reversia-btn reversia-btn-quiet reversia-btn-md click-effect"
 >
 <ClipboardList size={14} className="inline mr-1.5" />Детали задания
 </button>
 )}
 <B2BDocumentsBlock task={task} showToast={showToast} />
 
 {(user.role === "customer" || user.role === "admin") && task.status === "completed" && (
 <div className="flex flex-wrap gap-2">
 <button
 onClick={() =>confirmTask(task.id)}
 className="reversia-btn reversia-btn-green reversia-btn-md"
 >
 <CheckCircle size={14} className="inline mr-1.5" />Подтвердить выполнение
 </button>
 {!task.customer_revision_used && (
 <button
 data-testid={`request-revision-btn-${task.id}`}
 onClick={() =>openRevisionModal(task)}
 className="reversia-btn reversia-btn-md"
 style={{backgroundColor: '#d97706', color: '#fff', border: 'none'}}
 >
 <Wrench size={14} className="inline mr-1.5" />Вернуть на доработку
 </button>
 )}
 <button
 onClick={() =>openDisputeModal(task)}
 className="reversia-btn reversia-btn-orange reversia-btn-md"
 >
 <AlertCircle size={14} className="inline mr-1.5" />Подать жалобу
 </button>
 </div>
 )}
 </div>
 </div>
 ))}
 </div>
  <PaginationControls
   total={filteredMyTasks.length}
   perPage={perPage}
   currentPage={currentPage}
   onPageChange={setCurrentPage}
   onPerPageChange={setPerPage}
  />
 </>)}

 {/* Модальное окно возврата на доработку */}
 {showRevisionModal && (
 <div className="fixed inset-0 flex items-center justify-center p-4 z-50" style={{background:'rgba(0,0,0,0.55)'}}>
 <div className="reversia-card max-w-md w-full" data-testid="revision-modal">
 <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>
 <Wrench size={18} style={{color:'#d97706'}} />Вернуть на доработку
 </h3>

 <div className="mb-4">
 <p className="text-sm text-gray-600 mb-2">
 <strong>Задание:</strong> {revisionTask?.title}
 </p>
 <div className="px-3 py-2 rounded-lg text-sm" style={{background:'#fffbeb', border:'1px solid #fcd34d', color:'#78350f'}}>Доступно 1 раз, без участия администратора. У исполнителя будет 48 часов на исправление. Если после доработки результат снова не устроит — откройте спор, решение примет администратор.
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>Что нужно исправить
 </label>
 <textarea
 data-testid="revision-modal-comment"
 value={revisionComment}
 onChange={(e) =>setRevisionComment(e.target.value)}
 placeholder="Конкретно укажите, что именно нужно исправить или дополнить (например: не хватает 4 фотографий заднего двора)..."
 className="w-full p-2 rounded-md" style={{border:'1px solid var(--rv-border)', color:'var(--rv-text-primary)', fontFamily:'IBM Plex Sans,sans-serif'}}
 rows="4"
 />
 </div>

 <div className="flex justify-end space-x-3 mt-6">
 <button
 onClick={closeRevisionModal}
 className="reversia-btn reversia-btn-sm"
 style={{background:'var(--rv-paper)', color:'var(--rv-text-secondary)', border:'1px solid var(--rv-border)'}}
 >Отмена
 </button>
 <button
 data-testid="revision-modal-submit"
 onClick={submitRevisionRequest}
 disabled={revisionSubmitting}
 className="reversia-btn reversia-btn-sm"
 style={{backgroundColor:'#d97706', color:'#fff', border:'none'}}
 >
 {revisionSubmitting ? "Отправляем..." : "Вернуть на доработку"}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Модальное окно для подачи жалобы */}
 {showDisputeModal && (
 <div className="fixed inset-0 flex items-center justify-center p-4 z-50" style={{background:'rgba(0,0,0,0.55)'}}>
 <div className="reversia-card max-w-md w-full">
 <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>
 <AlertCircle size={18} style={{color:'var(--rv-warning,#d97706)'}} />Подать жалобу
 </h3>
 
 <div className="mb-4">
 <p className="text-sm text-gray-600 mb-2">
 <strong>Задание:</strong> {selectedDisputeTask?.title}
 </p>
 <p className="text-sm text-gray-600 mb-4">Опишите причину жалобы. Администрация рассмотрит её в течение 3 рабочих дней.
 </p>
 </div>

 <div className="space-y-4">
 <div>
 <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>Причина жалобы
 </label>
 <select
 value={disputeForm.reason}
 onChange={(e) =>setDisputeForm({...disputeForm, reason: e.target.value})}
 className="w-full p-2 rounded-md" style={{border:'1px solid var(--rv-border)', color:'var(--rv-text-primary)', fontFamily:'IBM Plex Sans,sans-serif'}}
 >
 <option value="">Выберите причину</option>
 <option value="fraud">Мошенничество</option>
 <option value="unethical">Неэтичное поведение</option>
 <option value="threats">Угрозы или оскорбления</option>
 <option value="rules_violation">Нарушение правил платформы</option>
 <option value="personal_data">Нарушение конфиденциальности данных</option>
 <option value="other">Другое</option>
 </select>
 </div>

 <div>
 <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>Подробное описание
 </label>
 <textarea
 value={disputeForm.description}
 onChange={(e) =>setDisputeForm({...disputeForm, description: e.target.value})}
 placeholder="Опишите детально в чем заключается проблема..."
 className="w-full p-2 rounded-md" style={{border:'1px solid var(--rv-border)', color:'var(--rv-text-primary)', fontFamily:'IBM Plex Sans,sans-serif'}}
 rows="4"
 />
 </div>
 </div>

 <div className="flex justify-end space-x-3 mt-6">
 <button
 onClick={closeDisputeModal}
 className="reversia-btn reversia-btn-sm"
 style={{background:'var(--rv-paper)', color:'var(--rv-text-secondary)', border:'1px solid var(--rv-border)'}}
 >Отмена
 </button>
 <button
 onClick={submitDispute}
 className="reversia-btn reversia-btn-sm reversia-btn-orange"
 >Подать жалобу
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};
